import type { ReactNode } from "react"
import { Sidebar } from "@/components/layout/Sidebar"
import { MobileNav } from "@/components/layout/MobileNav"
import { Clock } from "lucide-react"
import { useTheme } from "@/components/theme-provider"
import { Button } from "@/components/ui/button"
import { useLocation, Outlet } from "react-router-dom"
import { AvatarDropdown } from "@/components/layout/AvatarDropdown"
import { NotificationsDropdown } from "@/components/layout/NotificationsDropdown"
import { HelpDropdown } from "@/components/layout/HelpDropdown"
import { SupportWidget } from "@/components/layout/SupportWidget"
import { ScrollToTop } from "@/components/help/ScrollToTop"

import { CommandMenu } from "@/components/search/CommandMenu"
import { FloatingTimer } from "@/components/time-tracking/floating-timer"
import { OfflineBanner } from "@/components/layout/OfflineBanner"
import { useState } from "react"
import { useTranslation } from "react-i18next"

export default function Layout({ children }: { children?: ReactNode }) {
    const { t } = useTranslation()
    const { theme, setTheme } = useTheme()
    const location = useLocation()
    const [open, setOpen] = useState(false)

    // Determine title based on path
    const getPageTitle = () => {
        const path = location.pathname
        if (path === "/" || path === "/dashboard") return t('sidebar.home')
        if (path.startsWith("/history")) return t('sidebar.history')
        if (path.startsWith("/reports")) return t('sidebar.reports')
        if (path.startsWith("/projects")) return t('sidebar.projects')
        if (path.startsWith("/clients")) return t('sidebar.clients')
        if (path.startsWith("/team")) return t('sidebar.team')
        if (path.startsWith("/ai-assistant")) return t('sidebar.aiAssistant')
        if (path.startsWith("/billing")) return t('sidebar.billing')
        if (path.startsWith("/profile")) return t('sidebar.profile')
        if (path.startsWith("/approvals")) return t('sidebar.approvals')
        if (path.startsWith("/help")) return t('sidebar.help')
        return "Tempora"
    }

    return (
        <div className="flex h-screen bg-background text-foreground transition-colors duration-300 overflow-hidden">
            {/* Desktop Sidebar */}
            <Sidebar />

            <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
                {/* Header */}
                <header className="flex items-center justify-between px-3 py-3 sm:p-4 lg:p-6 pb-2 border-b border-border/50 lg:border-none backdrop-blur-sm sticky top-0 z-40 bg-background/80 lg:bg-transparent">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        {/* Mobile Logo — visible below 1024px */}
                        <div className="lg:hidden h-8 w-8 bg-primary rounded-lg flex items-center justify-center shadow-lg shadow-primary/20 shrink-0">
                            <Clock className="h-5 w-5 text-primary-foreground" />
                        </div>
                        {/* Title & User Info */}
                        <div className="flex-1 min-w-0 flex flex-col justify-center">
                            <h1 className="text-lg lg:text-xl font-bold tracking-tight text-foreground/90 overflow-hidden text-ellipsis whitespace-nowrap">
                                {getPageTitle()}
                            </h1>
                        </div>
                    </div>

                    <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-4 shrink-0 px-1 sm:px-0">
                        <div className="relative w-48 lg:w-64 hidden lg:block">
                            <Button
                                variant="outline"
                                className="relative h-9 w-full justify-start rounded-[0.5rem] bg-card/50 text-sm text-muted-foreground sm:pr-12 md:w-40 lg:w-64 border-none shadow-none ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                onClick={() => setOpen(true)}
                            >
                                <span className="hidden lg:inline-flex">{t('common.search')}...</span>
                                <span className="inline-flex lg:hidden">{t('common.search')}...</span>
                                <kbd className="pointer-events-none absolute right-1.5 top-1.5 hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
                                    <span className="text-xs">⌘</span>K
                                </kbd>
                            </Button>
                        </div>
                        <CommandMenu open={open} setOpen={setOpen} />

                        <div className="flex items-center gap-1 sm:gap-2">
                          <HelpDropdown />
                          <NotificationsDropdown />
                          <FloatingTimer />
                        </div>

                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-card/50 shrink-0"
                            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                        >
                            {theme === 'dark' ? '🌙' : '☀️'}
                        </Button>

                        <AvatarDropdown />
                    </div>
                </header>

                <OfflineBanner />

                {/* Content Area */}
                <main id="main-scroll" className="flex-1 overflow-auto scrollbar-hide pb-28 lg:pb-6" style={{ paddingBottom: 'calc(7rem + env(safe-area-inset-bottom, 0px))' }}>
                    <div className="max-w-7xl mx-auto w-full px-3 py-4 sm:px-4 lg:p-6">
                        {children || <Outlet />}
                    </div>
                    {/* Scroll-to-top: solo visible en artículos de ayuda */}
                    {location.pathname.startsWith('/help/') && location.pathname.split('/').length >= 4 && (
                        <ScrollToTop scrollContainerId="main-scroll" />
                    )}
                </main>

                {/* Mobile Navigation */}
                <MobileNav />
            </div>

            <SupportWidget />
        </div>
    )
}
